import React, { useCallback, useEffect, useState } from "react";
import MainBody from "../../components/MainBody/MainBody";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import { formatContactName, formatNumber } from "../../lib/Misc";
import { Download } from "lucide-react";
import { useMediaQuery } from "../../components/use-media-query";
import { MonthPicker } from "../../components/ui/monthpicker";
import CommonFilters from "../../components/CommonFilters";
import { useTrust } from "../../lib/context/TrustContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { useOrganization } from "../../lib/context/OrganisationContext";
import DropDownButton from "../../components/DropDownButton";

const TrialBalance = ({ org = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const month = query.get("month")
    ? new Date(
        `${query.get("month")?.split("-")[1]}-${
          query.get("month")?.split("-")[0]
        }-01`
      )
    : new Date();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { trustId: trustID } = useTrust(org);
  const [trustId, setTrustId] = useState(org ? "" : trustID);

  const [selectedMonth, setSelectedMonth] = React.useState(
    month ? month : new Date()
  );

  const [vendorId, setVendorId] = useState();
  const [contactId, setContactId] = useState();

  const [trustListData, setTrustListData] = useState([]);

  const [activeTrustId, setActiveTrustId] = React.useState(null);
  const { organizationData } = useOrganization();

  useEffect(() => {
    setTrustId(trustID);
  }, [trustID]);

  const fetchTustTrialBalanceReportData = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (trustId?.length > 0) {
        trustId?.map((item, index) =>
          formData.append(`trust_ids[${index}]`, item)
        );
      }
      formData.append("type", "trial-balance-report");

      formData.append(
        "organization_id",
        organizationData?.organizationId ? organizationData?.organizationId : ""
      );
      formData.append(
        "to",
        `${
          moment(selectedMonth).format("MM-YYYY")
            ? moment(selectedMonth).format("MM-YYYY")
            : ""
        }`
      );

      const { data } = await fileUpload(
        `${API.ORG_GENERAL_LEDGER_REPORT}`,
        formData
      );
      setTrustListData(data?.data);
      let activetTust = data?.data?.filter((item) => item?.transactions === 1);

      if (activetTust?.[0]?.id !== undefined) {
        setActiveTrustId(activetTust?.[0]?.id);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setReportData([]);
    }
  }, [trustId, organizationData?.organizationId, selectedMonth]);

  useEffect(() => {
    if (org) {
      fetchTustTrialBalanceReportData();
    }
  }, [org, fetchTustTrialBalanceReportData]);

  const handleAccordionChange = async (value) => {
    setReportData([]);
    if (!value) {
      setActiveTrustId(null);
      return;
    }

    setActiveTrustId(value);
    await getReportData(value);
  };

  const getReportData = useCallback(
    async (activeTrustId) => {
      try {
        setLoading(true);

        const formData = new FormData();
        formData.append(
          "to",
          `${
            moment(selectedMonth).format("MM-YYYY")
              ? moment(selectedMonth).format("MM-YYYY")
              : ""
          }`
        );
        formData.append("export", 0);
        formData.append(
          "trust_id",
          org && activeTrustId ? activeTrustId : trustId ? trustId : ""
        );
        if (vendorId?.length > 0) {
          vendorId?.map((item, index) =>
            formData.append(`vendor_ids[${index}]`, item)
          );
        }

        if (contactId?.length > 0) {
          contactId?.map((item, index) =>
            formData.append(`contact_ids[${index}]`, item)
          );
        }

        const { data } = await fileUpload(
          `${API.GET_TRIAL_BALANCE_DATA}`,
          formData
        );
        let totalDebit = 0;
        let totalCredit = 0;
        let customerReportData = [];
        if (data?.data?.contacts && data?.data?.contacts.length > 0) {
          customerReportData = data?.data?.contacts.map((item) => {
            // totalCredit += item?.closing_balance ? parseFloat(item?.closing_balance) : 0;
            let obj = {
              ...item,
            };

            obj.debit_amount =
              Number(item.closing_balance) > 0
                ? String(item.closing_balance)
                : 0;
            obj.credit_amount =
              Number(item.closing_balance) < 0
                ? String(item.closing_balance * -1)
                : 0;
            if (Number(item.closing_balance) > 0) {
              totalDebit += Number(item?.closing_balance);
            } else {
              totalCredit += Number(item?.closing_balance * -1);
            }
            return obj;
          });
        }
        let totalData = {
          total_name: "Total",
          total_debit_amount: totalDebit,
          total_credit_amount: totalCredit,
        };
        setReportData([...customerReportData, totalData]);
        setLoading(false);
      } catch (error) {
        setReportData([]);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedMonth, trustId, vendorId, contactId]
  );

  useEffect(() => {
    if (org) {
      if (activeTrustId) {
        getReportData(activeTrustId);
      }
    } else if (!org) {
      getReportData();
    }
  }, [getReportData, activeTrustId, org]);

  useEffect(() => {
    if (!org) {
      navigate(`?month=${moment(selectedMonth, "MM-YYYY").format("MM-YYYY")}`, {
        replace: true,
      });
    }
  }, [navigate, org, selectedMonth]);

  const reportGenerator = (quantity) => {
    const items = [];

    for (let i = 0; i < quantity; i++) {
      items.push({
        key: reportData[i]?.total_name
          ? reportData[i]?.total_name
          : reportData[i]?.net_name
          ? reportData[i]?.net_name
          : reportData[i]?.id
          ? reportData[i]?.id + String(reportData[i]?.is_bank_reconciliation)
          : "not-expand-row",
        batch_id: reportData[i]?.total_name ? (
          <div>2</div>
        ) : reportData[i]?.id ? (
          reportData[i]?.id
        ) : (
          ""
        ),
        is_reversed: reportData[i]?.is_reversed
          ? reportData[i]?.is_reversed
          : "",
        is_bank_reconciliation: reportData[i]?.is_bank_reconciliation,
        is_cash_transaction: reportData[i]?.is_cash_transaction,
        account: (
          <div
            className={
              reportData[i]?.total_name === "Total" ? "" : "all-client-name"
            }
          >
            {reportData[i]?.name ? formatContactName(
              reportData[i]?.type,
              {
                last_name: reportData[i]?.name,
              },
              organizationData?.organizationData
            ) : "Total"}
          </div>
        ),
        type: reportData[i]?.type,
        credit: reportData[i]?.total_credit_amount ? (
          `$${formatNumber(Number(reportData[i]?.total_credit_amount))}`
        ) : reportData[i]?.credit_amount ? (
          <span>${formatNumber(Number(reportData[i]?.credit_amount))}</span>
        ) : (
          "$0.00"
        ),

        debit: reportData[i]?.total_debit_amount ? (
          `$${formatNumber(Number(reportData[i]?.total_debit_amount))}`
        ) : reportData[i]?.debit_amount ? (
          <span>${formatNumber(Number(reportData[i]?.debit_amount))}</span>
        ) : (
          "$0.00"
        ),
      });
    }

    return items;
  };

  const reportDatas = reportGenerator(reportData?.length);

  /**
   * Table Heading Data
   */
  const columns = [
    {
      dataField: "account",
      text: "Account",
      headerClasses: THClasses,
      classes: TDClasses,
    },
    {
      dataField: "debit",
      text: "Debit",
      align: "right",
      headerClasses: THClasses,
      classes: TDClasses,
      headerAlign: (column, colIndex) => "right",
    },
    {
      dataField: "credit",
      text: "Credit",
      align: "right",
      headerClasses: THClasses,
      classes: TDClasses,
      headerAlign: (column, colIndex) => "right",
    },
  ];

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(
        "to",
        `${
          moment(selectedMonth).format("MM-YYYY")
            ? moment(selectedMonth).format("MM-YYYY")
            : ""
        }`
      );
      formData.append("export", 1);
      formData.append("format", format);
      if (org) {
        if (trustId?.length > 0) {
          trustId?.map((item, index) =>
            formData.append(`trust_ids[${index}]`, item)
          );
        }

        formData.append(
          "organization_id",
          organizationData?.organizationId
            ? organizationData?.organizationId
            : ""
        );
      } else if (!org) {
        formData.append(`trust_id`, trustId ? trustId : "");
      }
      if (vendorId?.length > 0) {
        vendorId?.map((item, index) =>
          formData.append(`vendor_ids[${index}]`, item)
        );
      }

      if (contactId?.length > 0) {
        contactId?.map((item, index) =>
          formData.append(`contact_ids[${index}]`, item)
        );
      }

      const { data } = await fileUpload(
        `${API.GET_TRIAL_BALANCE_DATA}`,
        formData
      );
      const export_report_path = data?.data?.file;
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute(
          "download",
          file[file.length - 1] || "transactionsreport.pdf"
        );
        a.setAttribute("target", "_blank");
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        search={false}
        actionRight={isDesktop ? true : false}
        actionContent={
          <>
            <MonthPicker
              onMonthSelect={setSelectedMonth}
              selectedMonth={selectedMonth}
              variant="outline"
              align={isDesktop ? "end" : "start"}
              maxDate={new Date()}
            />

            <CommonFilters
              trust={org}
              trustId={trustId}
              multiTrust={org}
              setTrustId={setTrustId}
              vendor={true}
              setVendorId={setVendorId}
              vendorId={vendorId}
              multiVendor={true}
              contact={true}
              setContactId={setContactId}
              contactId={contactId}
              multiContact={true}
            />

            <DropDownButton
              items={[
                {
                  label: "PDF",
                  onClick: () => handleExport("pdf"),
                  disabled: loading,
                },
                {
                  label: "Excel",
                  onClick: () => handleExport("xlsx"),
                  disabled: loading,
                },
              ]}
              text={
                <div className="flex gap-2 items-center">
                  Export <Download size={18} />
                </div>
              }
            />
          </>
        }
        loading={loading}
      >
        {org && trustListData?.length > 0 ? (
          <>
            <Accordion
              type="single"
              collapsible
              value={activeTrustId}
              onValueChange={handleAccordionChange}
              className="w-full"
            >
              {trustListData.map((item) => {
                const trustLabel = item?.business_name
                  ? item?.business_name
                  : item?.first_name
                  ? item?.first_name + " " + item?.last_name
                  : item?.last_name;

                return (
                  <AccordionItem
                    key={item?.id}
                    value={item?.id}
                    className="border-none mb-2"
                  >
                    <AccordionTrigger className="text-base font-medium bg-webSecondary hover:bg-webSecondary text-white dark:bg-muted px-4 py-2 rounded-md dark:hover:bg-muted/70">
                      <div className="flex items-center justify-between w-full pr-3">
                        {trustLabel}
                        <div className="flex gap-3">
                          <div>
                            Opening Balance:{" "}
                            <span>
                              {item?.opening_balance
                                ? item?.opening_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.opening_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.opening_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                          <div>
                            Closing Balance:{" "}
                            <span>
                              {item?.closing_balance
                                ? item?.closing_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.closing_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.closing_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="w-full">
                        <div className="lg:w-1/2 mx-auto">
                          <div className="custom-table mt-2 w-full">
                            <div
                              className={"table-body-class sm:max-h-[600px]"}
                            >
                              <BootstrapTable
                                keyField="key"
                                remote
                                data={reportDatas}
                                columns={columns}
                                rowClasses={(row) => {
                                  if (row?.key === "Total") {
                                    return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky bottom-0`;
                                  }
                                  if (row?.is_reversed) {
                                    return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                                  }
                                  return TRClasses;
                                }}
                                noDataIndication="No Data Found"
                                classes={TableClasses}
                                wrapperClasses={TableWrapClasses}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        ) : !org ? (
          <div className="w-full">
            <div className="lg:w-1/2 mx-auto">
              <div className="custom-table">
                <div className={"table-body-class sm:max-h-[730px]"}>
                  <BootstrapTable
                    keyField="key"
                    remote
                    data={reportDatas}
                    columns={columns}
                    rowClasses={(row) => {
                      if (row?.key === "Total") {
                        return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky bottom-0`;
                      }
                      if (row?.is_reversed) {
                        return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                      }
                      return TRClasses;
                    }}
                    noDataIndication="No Data Found"
                    classes={TableClasses}
                    wrapperClasses={TableWrapClasses}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </MainBody>
    </>
  );
};

export default TrialBalance;
